"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "./ui/Button";

/**
 * Compact in-app confirmation / alert (replaces window.confirm / alert).
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  alertOnly = false,
  confirmLoading = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !confirmLoading) onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onCancel, confirmLoading]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/45"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="absolute inset-0"
        onClick={confirmLoading ? undefined : onCancel}
        aria-hidden="true"
      />

      <div className="relative bg-background border border-border rounded-xl shadow-xl w-full max-w-[20rem] overflow-hidden">
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-start justify-between gap-2">
            <h3 id="confirm-dialog-title" className="text-sm font-semibold text-foreground leading-snug pr-6">
              {title}
            </h3>
            <button
              type="button"
              onClick={onCancel}
              disabled={confirmLoading}
              className="shrink-0 p-1 rounded-md hover:bg-muted transition-colors disabled:opacity-50 -mt-0.5 -mr-1"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
          {message ? (
            <p className="text-xs text-muted-foreground leading-relaxed mt-2">{message}</p>
          ) : null}
        </div>

        <div className="px-4 pb-4 flex justify-end gap-2">
          {!alertOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-[11px] font-semibold"
              onClick={onCancel}
              disabled={confirmLoading}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            type="button"
            variant={variant === "danger" ? "destructive" : "default"}
            size="sm"
            className="h-8 px-3 text-[11px] font-semibold"
            onClick={onConfirm}
            disabled={confirmLoading}
          >
            {confirmLoading ? "Please wait…" : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
